import { blogPosts, getAllCategories, getAllTags } from '@/data/blogPosts';

class BlogService {
    constructor() {
        this.baseUrl = '/blog';
        this.posts = [];
        this.categories = [];
        this.tags = [];
        this.initialized = false;
    }

    async initialize() {
        if (this.initialized) return;

        try {
            await this.loadAllPosts();
            this.initialized = true;
        } catch (error) {
            console.error('Failed to initialize blog service:', error);
            throw error;
        }
    }

    async loadAllPosts() {
        try {
            this.posts = blogPosts.sort((a, b) => new Date(b.date) - new Date(a.date));

            this.extractCategoriesAndTags();

            return this.posts;
        } catch (error) {
            console.error('Failed to load posts:', error);
            throw error;
        }
    }

    async loadPostMetadata(slug) {
        try {
            return this.posts.find((post) => post.slug === slug) || null;
        } catch (error) {
            console.error(`Failed to load metadata for ${slug}:`, error);
            return null;
        }
    }

    async loadPostContent(slug) {
        try {
            const response = await fetch(`${this.baseUrl}/${slug}/index.md`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.text();
        } catch (error) {
            console.error(`Failed to load content for ${slug}:`, error);
            throw error;
        }
    }

    async getPost(slug) {
        try {
            if (!this.initialized) {
                await this.initialize();
            }

            const metadata = this.posts.find((post) => post.slug === slug);
            if (!metadata) {
                throw new Error('Post not found');
            }

            const content = await this.loadPostContent(slug);

            return {
                ...metadata,
                content,
            };
        } catch (error) {
            console.error(`Failed to get post ${slug}:`, error);
            throw error;
        }
    }

    async getAllPosts(filters = {}) {
        if (!this.initialized) {
            await this.initialize();
        }

        let filteredPosts = [...this.posts];

        if (filters.category) {
            filteredPosts = filteredPosts.filter(
                (post) => post.category.toLowerCase() === filters.category.toLowerCase(),
            );
        }

        if (filters.tag) {
            filteredPosts = filteredPosts.filter((post) =>
                post.tags.some((tag) => tag.toLowerCase() === filters.tag.toLowerCase()),
            );
        }

        if (filters.featured) {
            filteredPosts = filteredPosts.filter((post) => post.featured);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filteredPosts = filteredPosts.filter(
                (post) =>
                    post.title.toLowerCase().includes(searchTerm) ||
                    post.description.toLowerCase().includes(searchTerm) ||
                    post.tags.some((tag) => tag.toLowerCase().includes(searchTerm)),
            );
        }

        const page = filters.page || 1;
        const perPage = filters.perPage || 9;
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;

        return {
            posts: filteredPosts.slice(startIndex, endIndex),
            pagination: {
                currentPage: page,
                perPage,
                total: filteredPosts.length,
                totalPages: Math.ceil(filteredPosts.length / perPage),
                hasNextPage: endIndex < filteredPosts.length,
                hasPrevPage: page > 1,
            },
        };
    }

    async getFeaturedPosts() {
        if (!this.initialized) {
            await this.initialize();
        }
        return this.posts.filter((post) => post.featured);
    }

    async getCategories() {
        if (!this.initialized) {
            await this.initialize();
        }
        return this.categories;
    }

    async getTags() {
        if (!this.initialized) {
            await this.initialize();
        }
        return this.tags;
    }

    async searchPosts(query) {
        if (!this.initialized) {
            await this.initialize();
        }

        const searchTerm = query.toLowerCase();
        return this.posts.filter(
            (post) =>
                post.title.toLowerCase().includes(searchTerm) ||
                post.description.toLowerCase().includes(searchTerm) ||
                post.tags.some((tag) => tag.toLowerCase().includes(searchTerm)),
        );
    }

    async getRelatedPosts(currentPost, limit = 3) {
        if (!this.initialized) {
            await this.initialize();
        }

        const relatedPosts = this.posts
            .filter((post) => post.id !== currentPost.id)
            .map((post) => {
                let score = 0;

                if (post.category === currentPost.category) {
                    score += 3;
                }

                const commonTags = post.tags.filter((tag) => currentPost.tags.includes(tag));
                score += commonTags.length;

                return { ...post, score };
            })
            .filter((post) => post.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, limit);

        return relatedPosts;
    }

    extractCategoriesAndTags() {
        this.categories = getAllCategories();
        this.tags = getAllTags();
    }

    async getPostById(id) {
        if (!this.initialized) {
            await this.initialize();
        }
        return this.posts.find((post) => post.id === id);
    }

    async getPostsByCategory(category) {
        if (!this.initialized) {
            await this.initialize();
        }
        return this.posts.filter((post) => post.category === category);
    }

    async getPostsByTag(tag) {
        if (!this.initialized) {
            await this.initialize();
        }
        return this.posts.filter((post) => post.tags && post.tags.includes(tag));
    }
}

const blogService = new BlogService();
export default blogService;
